import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AssignCustomerCase,
  AssignCustomerCaseInput,
  CustomerCase,
  EllipticTransaction,
  ServerResponse
} from '@shared/types';
import { map, Observable } from 'rxjs';
import { UtilsService } from './utils.service';

export interface CaseComment {
  comment_id: string;
  case_id: string;
  user_id: string;
  comment_text: string;
  case_status_at_time: string;
  timestamp: string;
}

@Injectable({ providedIn: 'root' })
export class CasesSharedService {
  private httpClient = inject(HttpClient);
  private utilsService = inject(UtilsService);

  /**
   * Assigns a case to a user.
   * used for customer cases, elliptic cases and travel rule cases
   * @param assignments The case assignments to be processed.
   * @returns An Observable that emits the server response.
   */
  public assignCase(
    assignments: AssignCustomerCaseInput | AssignCustomerCaseInput[]
  ): Observable<ServerResponse<AssignCustomerCase[]>> {
    const apiUrl = this.utilsService.buildApiUrl(`case/assign/`);
    const payload = Array.isArray(assignments) ? assignments : [assignments];
    return this.httpClient
      .post<ServerResponse<AssignCustomerCase[]>>(apiUrl, payload)
      .pipe(map((response) => response));
  }

  public postComment(
    caseId: number | string,
    userId: number,
    commentText: string
  ): Observable<CaseComment> {
    const apiUrl = this.utilsService.buildApiUrl(`case/${caseId}/comments`);
    return this.httpClient.post<CaseComment>(apiUrl, {
      user_id: userId,
      comment_text: commentText
    });
  }

  /**
   * Updates the due date for a case.
   * used for customer cases and elliptic cases
   * @param code The code of the case to update.
   * @param due_date The new due date for the case.
   * @returns An Observable that emits the server response.
   */
  public updateCaseDueDate(
    code: string,
    due_date: string
  ): Observable<ServerResponse<CustomerCase | EllipticTransaction>> {
    const apiUrl = this.utilsService.buildApiUrl(`case/${code}/due-date/`);
    return this.httpClient
      .patch<ServerResponse<CustomerCase | EllipticTransaction>>(apiUrl, { due_date })
      .pipe(map((response) => response));
  }
}
